import { useEffect, useRef } from "react"
import queryString from "query-string"
import { useHistory } from "react-router-dom"
import isEqual from "lodash/isEqual"
import { FiltersState } from "../states"
import {
  removeFixedFilters,
  replaceReadyFilterToWorkkeysSearchParams,
  replaceWorkkeysSearchParamsToReadyFilter,
  transformQueryFilters
} from "../helpers"

// Helper to remove empty arrays from filters object
const removeEmptyFilters = (filters: FiltersState): FiltersState => {
  return Object.fromEntries(
    Object.entries(filters).filter(
      ([_, value]) => Array.isArray(value) && value.length > 0
    )
  )
}

export const useQueryFilters = (
  filters: FiltersState,
  setFilters: React.Dispatch<React.SetStateAction<FiltersState>>,
  fixedFilters?: FiltersState
) => {
  const history = useHistory<{ setFilters?: boolean }>()

  const mountedRef = useRef(false)

  // Initially populate query from filters state.
  useEffect(() => {
    if (mountedRef.current) {
      // Filter out empty arrays before stringifying
      const cleanedFilters = removeEmptyFilters(
        removeFixedFilters(filters, fixedFilters)
      )

      // Replacing of Ready filters names to Workkeys search queries names
      const replacedFilters =
        replaceReadyFilterToWorkkeysSearchParams(cleanedFilters)

      history.replace({
        pathname: history.location.pathname,
        search: queryString.stringify(replacedFilters)
      })
    }

    mountedRef.current = true
  }, [filters, fixedFilters])

  // When query changes, setup filters.
  useEffect(() => {
    if (!mountedRef.current) {
      return
    }

    const queryFilters = queryString.parse(history.location.search)

    // Replacing of Workkeys search queries names to Ready filters names
    const replacedQueryFilters =
      replaceWorkkeysSearchParamsToReadyFilter(queryFilters)

    const newFilters = transformQueryFilters(replacedQueryFilters, fixedFilters)

    setFilters(prevState => {
      if (isEqual(newFilters, prevState)) {
        return prevState
      }

      return newFilters
    })
  }, [history.location.search, setFilters, fixedFilters])
}
